import React from 'react';

import IconUser from '@/assets/user.svg?react';

import { useFormDataQuery, useOffers } from '@/api/getters';
import { useDownloadOfferPdf } from '@/api/setters';
import IconCalendar from '@/assets/calendar.svg?react';
import IconCloudDownload from '@/assets/cloud-download.svg?react';
import IconFolder from '@/assets/folder.svg?react';
import IconInfo from '@/assets/info-shape.svg?react';
import Opinion from '@/components/Opinion';
import Button, { ButtonLoading } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  Cell,
  Paper,
  Title,
} from '@/features/v2/summaryForms/components/offer/Offers';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';

const ClientCardOffer = ({
  isMobileMenu,
  withPdf,
}: {
  isMobileMenu?: boolean;
  withPdf?: boolean;
}) => {
  const { data, isLoading } = useOffers();
  const { i18n } = useLingui();
  const { data: formData } = useFormDataQuery();
  const downloadPdfMutation = useDownloadOfferPdf();

  const handleDownloadPdf = async () => {
    if (formData?.id) {
      await downloadPdfMutation.mutateAsync(formData?.id, {
        onSuccess: (data) => {
          const url = URL.createObjectURL(data);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'propozycja-helpfind.pdf';
          a.click();
        },
      });
    }
  };

  if (isLoading || !data)
    return (
      <Paper
        className={cn(
          'lg:gap-[22px] lg:px-10 lg:pb-[30px] lg:pt-9',
          isMobileMenu && 'rounded-lg border-t-0 px-6 pb-6'
        )}
      >
        <Title
          icon={
            <IconUser className="size-5 items-center justify-center text-primary lg:size-[26px]" />
          }
        >
          {i18n.t('Dane Twojej sprawy')}
        </Title>

        <div
          data-id="content"
          className="flex flex-col gap-2 lg:flex-row lg:gap-4"
        >
          {[0, 1, 2].map((item) => (
            <div key={item} className="flex gap-2">
              <Skeleton key={item} className="size-[16px]" />
              <Skeleton key={item} className="h-[18px] w-[200px] lg:h-[20px]" />
            </div>
          ))}
        </div>
      </Paper>
    );

  return (
    <Paper
      className={cn(
        'lg:gap-[22px] lg:px-10 lg:pb-[30px] lg:pt-9',
        isMobileMenu && 'rounded-lg border-t-0 px-6 pb-6'
      )}
    >
      <Title
        icon={
          <IconUser className="size-5 items-center justify-center text-primary lg:size-[26px]" />
        }
      >
        {i18n.t('Dane Twojej sprawy')}
      </Title>
      <div
        data-id="content"
        className="flex flex-col gap-2 lg:flex-row lg:gap-4"
      >
        <Cell
          icon={<IconFolder className="min-w-4 text-primary" width={16} />}
          label={'Towarzystwo ubezpieczeń: '}
          value={data?.accident.insurerCompany ?? '—'}
          className="break-words md:order-1"
          disableTooltip={
            !data?.accident.insurerCompany ||
            data?.accident.insurerCompany?.length < 15
          }
        />
        <Cell
          icon={<IconCalendar className="min-w-4 text-primary" width={16} />}
          label={'Data szkody: '}
          value={data?.accident.date ? `${data?.accident.date} r.` : '—'}
          className="break-words md:order-1"
          disableTooltip={
            !data?.accident.date || data?.accident.date?.length < 15
          }
        />
        <Cell
          icon={<IconInfo className="min-w-4 text-primary" width={16} />}
          label={'Nr szkody: '}
          value={data?.accident.nrDamageFile ?? '—'}
          className="break-words md:order-1"
          disableTooltip={
            !data?.accident.nrDamageFile ||
            data?.accident.nrDamageFile?.length < 15
          }
        />
      </div>
      {isMobileMenu && (
        <>
          {withPdf && (
            <Button
              onClick={handleDownloadPdf}
              loading={downloadPdfMutation.isPending}
            >
              {downloadPdfMutation.isPending ? (
                <ButtonLoading />
              ) : (
                <IconCloudDownload className="size-[18px]" />
              )}
              {i18n.t('Pobierz PDF')}
            </Button>
          )}
          <p
            className={cn(
              'caption-large flex justify-center text-center text-dark-gray-400 lg:hidden',
              !withPdf && 'order-last'
            )}
          >
            {i18n.t('Numer Twojej propozycji:')}{' '}
            {formData?.offer.opportunity_id ?? '—'}
          </p>
          <div className="lg:hidden">
            <Opinion />
          </div>
        </>
      )}
    </Paper>
  );
};

export default ClientCardOffer;
